import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Button } from '../ui.tsx';
import type { Company } from '@shared/types/index.ts';

/**
 * The company record's settings used to be edited through one form spread
 * across four cards, saved by a bar fixed to the viewport's foot. That shape
 * had a real cost: pressing Save committed every half-typed edit on the page,
 * and a validation error could land in a card three screens away from the
 * button that caused it.
 *
 * `PATCH /company` accepts any subset of fields, so each card is now its own
 * form: it drafts only the fields it shows, saves only those, and shows its
 * outcome inside its own borders. This hook is the shared machinery.
 *
 * The adoption guard is inherited from the old page-level form, shrunk to one
 * card. The session query owns the canonical company record, and plenty of
 * OTHER things on the page refresh it when they save — the widget's style,
 * a logo upload, another card. An unconditional reset here would silently
 * discard this card's half-typed edits whenever one of them did. So a clean
 * card adopts every refresh; a dirty one only adopts the refresh its own save
 * asked for.
 */
export interface CompanyCardForm<T> {
  /** This card's editable slice, or null while the session is still loading. */
  draft: T | null;
  set: <K extends keyof T>(key: K, value: T[K]) => void;
  /** Whether anything has been edited since the last save or adoption. */
  dirty: boolean;
  readOnly: boolean;
  submit: () => void;
  discard: () => void;
  saving: boolean;
  saved: boolean;
  error: string | null;
  fieldErrors: Record<string, string>;
}

export function useCompanyCardForm<T extends object>(
  /** Must be referentially stable — define it at module scope. */
  snapshot: (company: Company) => T,
  /** Turns the draft into the partial PATCH body. Also module scope. */
  toPayload: (draft: T) => Record<string, unknown>,
): CompanyCardForm<T> {
  const { t } = useTranslation();
  const { company, can, refresh } = useSession();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState<T | null>(() => (company ? snapshot(company) : null));
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const draftRef = useRef(draft);
  draftRef.current = draft;
  /** The slice this card last adopted — reference identity is the dirty flag. */
  const lastAdopted = useRef(draft);
  /**
   * The exact draft object the in-flight save sent. Adoption after our own
   * save is conditional on the draft still BEING that object: the fields stay
   * editable while the request runs, and an edit made mid-flight must survive
   * the refresh (still dirty, still saveable) rather than being replaced by
   * the older snapshot the server just echoed back.
   */
  const submitted = useRef<T | null>(null);

  useEffect(() => {
    const next = company ? snapshot(company) : null;
    const cleanOrJustSaved =
      !draftRef.current ||
      draftRef.current === lastAdopted.current ||
      (submitted.current !== null && draftRef.current === submitted.current);
    if (cleanOrJustSaved) setDraft(next);
    submitted.current = null;
    lastAdopted.current = next;
  }, [company, snapshot]);

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) => companyApi.update(payload),
    onMutate: () => {
      submitted.current = draftRef.current;
    },
    onSuccess: async () => {
      setSaved(true);
      setError(null);
      setFieldErrors({});
      // The session owns the company record, so refreshing it is most of the
      // job. Locations subscribe indirectly: each carries what its overrides
      // resolve to against the company, and this save may have moved the
      // values they inherit — hours, tax, timezone. Cards that cannot touch an
      // inherited field still invalidate; a spare refetch of a small list is
      // cheaper than a card knowing which fields locations inherit.
      await refresh();
      await queryClient.invalidateQueries({ queryKey: ['locations'] });
      // The payment-status answer carries the effective tips switch a save
      // may have flipped; the record-payment forms show or hide their tip
      // field from it. Invalidated from every card for the same reason as
      // the locations list above — a spare refetch beats a card knowing
      // which company fields that answer resolves from.
      await queryClient.invalidateQueries({ queryKey: ['payments-status'] });
    },
    onError: (caught) => {
      setSaved(false);
      // A failed save wrote nothing; the draft is still unsaved work and no
      // later refresh may treat it as "just saved".
      submitted.current = null;
      if (caught instanceof ApiRequestError) {
        setFieldErrors(caught.fields);
        setError(Object.keys(caught.fields).length > 0 ? null : caught.message);
      } else {
        setError(t('settings.saveError'));
      }
    },
  });

  return {
    draft,
    set: (key, value) => {
      setDraft((current) => (current ? { ...current, [key]: value } : current));
      setSaved(false);
    },
    dirty: draft !== lastAdopted.current,
    readOnly: !can('company:write'),
    submit: () => {
      if (draftRef.current) save.mutate(toPayload(draftRef.current));
    },
    discard: () => {
      setDraft(lastAdopted.current);
      setFieldErrors({});
      setError(null);
      setSaved(false);
    },
    saving: save.isPending,
    saved,
    error,
    fieldErrors,
  };
}

/**
 * The card's outcome row: result alerts, then Discard (only while there is
 * something to throw away) and a Save that names the card it saves — six
 * buttons reading "Save" would leave a screen reader's button list saying
 * nothing at all.
 *
 * The Save button is always present rather than appearing on edit: a control
 * that materializes is a control nobody knew was coming, and the disabled
 * state is what says "nothing to save yet".
 */
export function CardSaveFooter<T>({
  form,
  saveLabel,
  onSubmit,
}: {
  form: CompanyCardForm<T>;
  saveLabel: string;
  /** Replaces the save click when the card must ask something before it writes. */
  onSubmit?: () => void;
}) {
  const { t } = useTranslation();
  if (form.readOnly) return null;
  return (
    <div className="mt-4">
      {form.error ? (
        <div className="mb-3">
          <Alert tone="error">{form.error}</Alert>
        </div>
      ) : null}
      {form.saved ? (
        <div className="mb-3">
          <Alert tone="success">{t('settings.saved')}</Alert>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          busy={form.saving}
          disabled={!form.dirty && !form.saving}
          onClick={onSubmit ?? form.submit}
        >
          {saveLabel}
        </Button>
        {form.dirty && !form.saving ? <Button onClick={form.discard}>{t('settings.discard')}</Button> : null}
      </div>
    </div>
  );
}
